import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { useAppSelector } from "../../../app/hooks";
import { selectLabels } from "../../../slices/language";
import IconifyIcon from "../../IconifyIcon";
import {
  createEmptyLocalRequirementType,
  LocalRequirementType,
  RequirementTypeFormDataType,
} from "../../../types/localRequirementType";

interface RequirementTypeFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (requirement: LocalRequirementType) => void;
  initialValue?: LocalRequirementType | null;
}

const RequirementTypeFormModal = ({
  open,
  onClose,
  onSave,
  initialValue,
}: RequirementTypeFormModalProps) => {
  const labels = useAppSelector(selectLabels);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [dataType, setDataType] =
    React.useState<RequirementTypeFormDataType>("string");
  const [required, setRequired] = React.useState(false);
  const [selectorData, setSelectorData] = React.useState<string[]>(["", ""]);

  React.useEffect(() => {
    if (!open || dataType !== "selector") return;
    setSelectorData((prev) => {
      if (prev.length >= 2) return prev;
      const next = [...prev];
      while (next.length < 2) next.push("");
      return next;
    });
  }, [open, dataType]);

  React.useEffect(() => {
    if (!open) return;

    if (initialValue) {
      setName(initialValue.name);
      setDescription(initialValue.description);
      setDataType(
        initialValue.data_type === "boolean" ? "string" : initialValue.data_type,
      );
      setRequired(initialValue.required);
      setSelectorData(
        initialValue.selector_data.length >= 2
          ? initialValue.selector_data
          : initialValue.selector_data.length === 1
            ? [...initialValue.selector_data, ""]
            : ["", ""],
      );
      return;
    }

    const empty = createEmptyLocalRequirementType("temp");
    setName(empty.name);
    setDescription(empty.description);
    setDataType(empty.data_type);
    setRequired(empty.required);
    setSelectorData(empty.selector_data);
  }, [open, initialValue]);

  const handleSave = () => {
    if (!name.trim()) return;

    const cleanedSelectorData = selectorData
      .map((option) => option.trim())
      .filter(Boolean);

    if (dataType === "selector" && cleanedSelectorData.length < 2) {
      return;
    }

    onSave({
      localId:
        initialValue?.localId ??
        `local-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      serverId: initialValue?.serverId,
      name: name.trim(),
      description: description.trim(),
      data_type: dataType,
      required,
      access_key: false,
      transaction: true,
      selector_data: dataType === "selector" ? cleanedSelectorData : [],
    });
    onClose();
  };

  const updateSelectorOption = (index: number, value: string) => {
    setSelectorData((prev) =>
      prev.map((option, optionIndex) =>
        optionIndex === index ? value : option,
      ),
    );
  };

  const addSelectorOption = () => {
    setSelectorData((prev) => [...prev, ""]);
  };

  const removeSelectorOption = (index: number) => {
    setSelectorData((prev) =>
      prev.length > 2
        ? prev.filter((_, optionIndex) => optionIndex !== index)
        : prev,
    );
  };

  const cleanedSelectorData = selectorData
    .map((option) => option.trim())
    .filter(Boolean);

  const canSave =
    Boolean(name.trim()) &&
    (dataType !== "selector" || cleanedSelectorData.length >= 2);

  const formControlLabelSx = {
    width: "fit-content",
    margin: 0,
    alignSelf: "flex-start",
  };

  const stopLabelToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const switchLabelWithInfo = (label: string, tooltip: string) => (
    <span className="flex align-items-center gap-1">
      {label}
      <Tooltip title={tooltip} arrow placement="top">
        <span
          className="flex align-items-center"
          style={{ lineHeight: 0 }}
          onMouseDown={stopLabelToggle}
          onClick={stopLabelToggle}
        >
          <IconifyIcon
            icon={"material-symbols:info"}
            style={{ fontSize: 20, color: "#838383" }}
          />
        </span>
      </Tooltip>
    </span>
  );

  const selectorOptionsSection =
    dataType === "selector" ? (
      <div>
        <p className="t-nowrap body-text-xs mb-3">
          {labels.selector_options} {`(${labels.two_min_options})`}
        </p>
        <div className="flex flex-dir-col gap-3">
          {selectorData.map((option, index) => (
            <Box
              key={`selector-option-${index}`}
              className="flex align-items-center gap-2"
            >
              <TextField
                fullWidth
                size="small"
                value={option}
                placeholder={`${labels.selector} ${index + 1}`}
                onChange={(event) =>
                  updateSelectorOption(index, event.target.value)
                }
              />
              {selectorData.length > 2 && (
                <IconButton
                  size="small"
                  onClick={() => removeSelectorOption(index)}
                  aria-label={labels.delete}
                >
                  <IconifyIcon icon={"material-symbols:close"} />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            size="small"
            variant="text"
            startIcon={<IconifyIcon icon={"ic:baseline-plus"} />}
            onClick={addSelectorOption}
            sx={{ alignSelf: "flex-end" }}
          >
            {labels.add_option}
          </Button>
        </div>
      </div>
    ) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{labels.add_custom_field}</span>
        <IconButton size="small" onClick={onClose} aria-label={labels.cancel}>
          <IconifyIcon icon={"mingcute:close-fill"} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div className="flex flex-dir-col gap-6">
          <div>
            <p className="t-nowrap body-text-xs mb-3">
              {labels.requirement_name} *
            </p>
            <TextField
              fullWidth
              size="small"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div>
            <p className="t-nowrap body-text-xs mb-3">
              {labels.requirement_description}
            </p>
            <TextField
              fullWidth
              size="small"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div>
            <p className="t-nowrap body-text-xs mb-3">
              {labels.requirement_data_type} *
            </p>
            <FormControl fullWidth size="small">
              <Select
                labelId="requirement-data-type-label"
                value={dataType}
                onChange={(event) =>
                  setDataType(event.target.value as RequirementTypeFormDataType)
                }
              >
                <MenuItem value="string">{labels.text}</MenuItem>
                <MenuItem value="selector">{labels.selector}</MenuItem>
              </Select>
            </FormControl>
          </div>
          {selectorOptionsSection}
          <FormControlLabel
            sx={formControlLabelSx}
            control={
              <Switch
                checked={required}
                onChange={(event) => setRequired(event.target.checked)}
              />
            }
            label={switchLabelWithInfo(
              labels.requirement_required,
              labels.requirement_required_tooltip,
            )}
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{labels.cancel}</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {labels.add}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequirementTypeFormModal;
